import { isEmpty } from 'lodash-es';
import { forwardRef, type PropsWithChildren, useRef } from 'react';

import { ANNOTATIONS_PADDING_BOTTOM } from './components/annotations-histogram/constants.js';
import { HistogramAnnotationsArea } from './components/annotations-histogram/HistogramAnnotationsArea.js';
import { HistogramAnnotationsIndicators } from './components/annotations-histogram/HistogramAnnotationsIndicators.js';
import { HistogramValueBottomAxisArea } from './components/axes/HistogramValueBottomAxisArea/HistogramValueBottomAxisArea.js';
import { HistogramValueLeftAxisArea } from './components/axes/HistogramValueLeftAxisArea.js';
import { HistogramValueRightAxisArea } from './components/axes/HistogramValueRightAxisArea.js';
import { HistogramChartPlot } from './components/layout/HistogramChartPlot.js';
import { HistogramOverlays } from './components/overlays/HistogramOverlays.js';
import { HistogramThresholdArea } from './components/thresholds/HistogramThresholdArea.js';
import { ThresholdIndicators } from './components/thresholds/ThresholdIndicators.js';
import {
  DISPLAY_NAME,
  HISTOGRAM_CHART_ARIA_LABEL,
} from './constants/defaults.js';
import { AREAS } from './constants/histogram-grid-area-rules.js';
import { useHistogramAnnotationsConfig } from './hooks/useHistogramAnnotationsConfig.js';
import { useHistogramFilteredData } from './hooks/useHistogramFilteredData.js';
import { useHistogramLegend } from './hooks/useHistogramLegend.js';
import { useHistogramThresholds } from './hooks/useHistogramThresholds.js';
import { useYAxisConfig } from './hooks/useYAxisConfig.js';
import { HistogramAnnotationsDataProvider } from './providers/HistogramAnnotationsDataProvider.js';
import { HistogramChartImperativeHandler } from './providers/HistogramChartImperativeHandler.js';
import { HistogramFormatterProvider } from './providers/HistogramFormatterProvider.js';
import { HistogramXScaleProvider } from './providers/HistogramXScaleProvider.js';
import { HistogramYScalesProvider } from './providers/HistogramYScalesProvider.js';
import type { HistogramChartConfiguration } from './types/histogram-props.js';
import type { HistogramChartRef } from './types/histogram-ref.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { defaultChartSize } from '../../core/styles/defaultChartValues.sty.js';
import { SCROLLBAR_WIDTH } from '../core/components/annotations/constants.js';
import { getAnnotationsVisibleArea } from '../core/components/annotations/utils/get-annotations-visible-area.js';
import { ChartGrid } from '../core/components/chart-grid/ChartGridRoot.js';
import { useChartLayoutLoading } from '../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { useGraphSize } from '../core/components/chart-layout/hooks/useGraphSize.js';
import { ChartLoaderOverlay } from '../core/components/chart-loader-overlay/ChartLoaderOverlay.js';
import {
  BOTTOM_PADDING_ADJUSTMENT,
  OVERLAY_PADDING,
} from '../core/components/data-point/constants.js';

const histogramGridTemplateAreas = `
  "${AREAS.annotations} ${AREAS.annotations} ${AREAS.annotations} . ."
  "${AREAS.leftAxis} ${AREAS.leftThreshold} plot ${AREAS.rightThreshold} ${AREAS.rightAxis}"
  ". . ${AREAS.bottomAxis} . ."
`;
const histogramGridTemplateColumns =
  'minmax(0, auto) auto minmax(60%, 1fr) auto minmax(0, auto)';

export interface _HistogramChartProps {
  config: HistogramChartConfiguration;
}

export const HistogramChartInternal = forwardRef<
  HistogramChartRef,
  PropsWithChildren<_HistogramChartProps>
>(({ config }, forwardedRef) => {
  const loading = useChartLayoutLoading();

  const annotations = useHistogramAnnotationsConfig();

  const tracksQuantity = annotations.tracks.length;
  const numHiddenTracks = annotations.tracks.filter(
    (value) => value.hidden,
  ).length;

  const annotationsVisibleArea = getAnnotationsVisibleArea(
    tracksQuantity,
    annotations?.visibleTracksLimit,
    numHiddenTracks,
  );

  const histogramGridTemplateRows = `minmax(0, ${
    annotationsVisibleArea + (tracksQuantity ? ANNOTATIONS_PADDING_BOTTOM : 0)
  }px) minmax(60%, 1fr) minmax(0, auto)`;

  const { leftThreshold, rightThreshold } = useHistogramThresholds();
  const hasThresholds = !isEmpty(leftThreshold) || !isEmpty(rightThreshold);
  const { width, height } = useGraphSize();

  const chartRef = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs<HistogramChartRef | HTMLDivElement>([
    forwardedRef,
    chartRef,
  ]);

  const { orientation } = useChartLayoutOrientation();
  const { right: axisRight } = useYAxisConfig();
  const { hidden: legendHidden } = useHistogramLegend();
  const { units } = useHistogramFilteredData();

  const availableWidth =
    tracksQuantity && orientation === 'vertical'
      ? width - SCROLLBAR_WIDTH
      : width;

  const noAxisRight = !axisRight || units.length === 1;

  const noLegendRight = legendHidden || orientation === 'vertical';
  const noLegendBottom = legendHidden || orientation === 'horizontal';

  const hasRightPadding = noAxisRight && noLegendRight;

  const chartGridHeight =
    height -
    (!tracksQuantity || noLegendBottom
      ? OVERLAY_PADDING + BOTTOM_PADDING_ADJUSTMENT
      : 0);

  return (
    <div
      style={{
        paddingTop: !tracksQuantity ? OVERLAY_PADDING : 0,
        paddingLeft: 0,
      }}
      className={defaultChartSize}
      role="img"
      aria-label={HISTOGRAM_CHART_ARIA_LABEL}
    >
      <ChartGrid
        width={availableWidth - (hasRightPadding ? OVERLAY_PADDING : 0)}
        height={chartGridHeight}
        gridTemplateAreas={histogramGridTemplateAreas}
        gridTemplateColumns={histogramGridTemplateColumns}
        gridTemplateRows={histogramGridTemplateRows}
        ref={mergedRef}
        data-testid="histogram-chart-root"
      >
        <HistogramXScaleProvider>
          <HistogramYScalesProvider>
            <HistogramFormatterProvider>
              <HistogramAnnotationsDataProvider>
                <HistogramChartImperativeHandler
                  config={config}
                  forwardedRef={forwardedRef}
                  chartRef={chartRef}
                >
                  <HistogramValueLeftAxisArea />
                  <HistogramValueBottomAxisArea />
                  <HistogramValueRightAxisArea />
                  {!!tracksQuantity && <HistogramAnnotationsArea />}
                  <ChartGrid.PlotArea>
                    <HistogramChartPlot />
                    <HistogramAnnotationsIndicators />
                    <ThresholdIndicators />
                    <ChartLoaderOverlay
                      loading={!!loading}
                      zoomEnabled={!!config.interactions?.zoom?.enabled}
                    >
                      <HistogramOverlays />
                    </ChartLoaderOverlay>
                  </ChartGrid.PlotArea>
                  {hasThresholds && <HistogramThresholdArea />}
                </HistogramChartImperativeHandler>
              </HistogramAnnotationsDataProvider>
            </HistogramFormatterProvider>
          </HistogramYScalesProvider>
        </HistogramXScaleProvider>
      </ChartGrid>
    </div>
  );
});

(
  HistogramChartInternal as typeof HistogramChartInternal & {
    displayName: string;
  }
).displayName = DISPLAY_NAME;
